import BaseAPIService from './baseAPI';

// Integration Types to match backend response
export interface ConnectedAccount {
  platform: 'linkedin' | 'x' | 'instagram' | 'facebook';
  connected: boolean;
  needs_reauth?: boolean;
  user_info?: {
    id?: string;
    name?: string;
    username?: string;
    email?: string;
    user_id?: string;
  } | null;
  connected_at?: string | null;
  last_validated?: string | null;
  message?: string;
}

export interface PlatformStatus {
  platform: string;
  connected: boolean;
  needs_reauth?: boolean;
  user_info?: ConnectedAccount['user_info'];
  message?: string;
}

export interface AccountsResponse {
  accounts: ConnectedAccount[];
  total_connected: number;
}

// OAuth response - direct from backend (no data wrapper)
export interface OAuthResponse {
  url: string;
  platform: string;
  state: string;
  redirect_uri?: string;
  setup_hint?: string;
}

export interface PostResponse {
  success: boolean;
  post_id?: string;
  platform_url?: string;
  message?: string;
}

export interface PostRequest {
  content: string;
  image_url?: string;
  platforms?: string[];
}

export interface OAuthTokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  platform: string;
  user_info?: Record<string, unknown>;
}

class IntegrationsAPIService extends BaseAPIService {
  constructor() {
    super();
  }

  // Get connected accounts
  async getConnectedAccounts(): Promise<AccountsResponse> {
    return this.get<AccountsResponse>('/integrations/accounts');
  }

  async getPlatformStatus(platform: string): Promise<PlatformStatus> {
    return this.get<PlatformStatus>(`/integrations/${platform}/status`);
  }

  // LinkedIn Integration
  async getLinkedInAuthUrl(): Promise<OAuthResponse> {
    return this.get<OAuthResponse>('/integrations/linkedin/auth');
  }

  async postToLinkedIn(postData: {
    content: string;
    image_url?: string;
  }): Promise<{ data: PostResponse }> {
    return this.post<{ data: PostResponse }>('/integrations/post-to/linkedin', postData);
  }

  async disconnectLinkedIn(): Promise<{ message: string }> {
    return this.delete<{ message: string }>('/integrations/linkedin/disconnect');
  }

  // X (Twitter) Integration
  async getXAuthUrl(): Promise<OAuthResponse> {
    return this.get<OAuthResponse>('/integrations/x/auth');
  }

  async postToX(postData: {
    content: string;
    image_url?: string;
  }): Promise<{ data: PostResponse }> {
    return this.post<{ data: PostResponse }>('/integrations/post-to/x', postData);
  }

  async disconnectX(): Promise<{ message: string }> {
    return this.delete<{ message: string }>('/integrations/x/disconnect');
  }

  // Instagram Integration
  async getInstagramAuthUrl(): Promise<OAuthResponse> {
    return this.get<OAuthResponse>('/integrations/instagram/auth');
  }

  async postToInstagram(postData: {
    content: string;
    image_url?: string;
  }): Promise<{ data: PostResponse }> {
    return this.post<{ data: PostResponse }>('/integrations/post-to/instagram', postData);
  }

  async disconnectInstagram(): Promise<{ message: string }> {
    return this.delete<{ message: string }>('/integrations/instagram/disconnect');
  }

  // Facebook Integration
  async getFacebookAuthUrl(): Promise<OAuthResponse> {
    return this.get<OAuthResponse>('/integrations/facebook/auth');
  }

  async postToFacebook(postData: {
    content: string;
    image_url?: string;
  }): Promise<{ data: PostResponse }> {
    return this.post<{ data: PostResponse }>('/integrations/post-to/facebook', postData);
  }

  async disconnectFacebook(): Promise<{ message: string }> {
    return this.delete<{ message: string }>('/integrations/facebook/disconnect');
  }

  // Multi-platform posting
  async postToMultiplePlatforms(postData: PostRequest): Promise<{
    data: {
      results: Array<{
        platform: string;
        success: boolean;
        post_id?: string;
        platform_url?: string;
        error?: string;
      }>;
    };
  }> {
    return this.post<any>('/integrations/post-to/multiple', postData);
  }

  // OAuth callback verification (exchange code for token via backend)
  async exchangeOAuthToken(platform: string, params: {
    code: string;
    state: string;
  }): Promise<OAuthTokenResponse> {
    return this.post<OAuthTokenResponse>(`/integrations/${platform}/token`, params);
  }

  /** @deprecated Use exchangeOAuthToken */
  async verifyOAuthCallback(platform: string, params: {
    code: string;
    state: string;
  }): Promise<{ data: ConnectedAccount }> {
    await this.exchangeOAuthToken(platform, params);
    const accounts = await this.getConnectedAccounts();
    const account = accounts.accounts.find((a) => a.platform === platform);
    return { data: account as ConnectedAccount };
  }

  async refreshConnection(platform: string): Promise<PlatformStatus> {
    return this.getPlatformStatus(platform);
  }
}

export const integrationsAPI = new IntegrationsAPIService();
export default integrationsAPI; 